import { type FormEvent, useId, useState } from "react";
import {
  COVERAGE_PAYER_KIND_LABELS,
  type CoveragePayerKind,
  CoveragePayerKindSchema,
  type WalkInIntakeInput,
  WalkInIntakeInputSchema,
} from "../api/clinicalWorkspace";
import type { HealthCardPhotoCaptureClient } from "../api/healthCardPhotos";
import type { RegistrationPrefill } from "../emr-import/extraction";
import type { PreparedHealthCardPhoto } from "../lib/healthCardPhoto";
import { HealthCardPhotoField } from "./HealthCardPhotoField";
import { IconClose } from "./IceBlueGlyphs";

const COVERAGE_PAYER_KINDS = CoveragePayerKindSchema.options;

/**
 * Where a health card photo can be taken (PA-13): the settings read that says whether capture is
 * available, for this site. Without it the form offers no photo.
 */
export interface WalkInHealthCardPhotos {
  readonly client: HealthCardPhotoCaptureClient;
  readonly siteId: string;
}

interface WalkInFormProps {
  readonly formId?: string;
  readonly embedded?: boolean;
  readonly submitting: boolean;
  readonly error: string | null;
  /**
   * The checked intake, and the photo of the front of the health card when one was taken for a
   * provincial plan. The page sends the photo once the chart exists; it never holds up the visit.
   */
  readonly onSubmit: (input: WalkInIntakeInput, photo?: PreparedHealthCardPhoto) => void;
  readonly onCancel: () => void;
  readonly healthCardPhotos?: WalkInHealthCardPhotos | undefined;
  /**
   * Values to start from: the identity, contact and coverage another EMR's
   * chart page gave (EXECDEV-227). Read once; give the form a new key to start
   * again from other values.
   */
  readonly initial?: RegistrationPrefill;
  /** Everything read-only, e.g. once the walk-in exists and only its imported entries are left. */
  readonly disabled?: boolean;
}

/** Persisted walk-in intake form mirroring the api.create_walk_in input contract. */
export function WalkInForm({
  formId,
  embedded = false,
  submitting,
  error,
  onSubmit,
  onCancel,
  healthCardPhotos,
  initial,
  disabled = false,
}: WalkInFormProps) {
  const titleId = useId();
  const [legalFirstName, setLegalFirstName] = useState(initial?.legalFirstName ?? "");
  const [legalMiddleName, setLegalMiddleName] = useState(initial?.legalMiddleName ?? "");
  const [legalLastName, setLegalLastName] = useState(initial?.legalLastName ?? "");
  const [preferredName, setPreferredName] = useState(initial?.preferredName ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(initial?.dateOfBirth ?? "");
  // The walk-in intake records female, male or unknown.
  const [administrativeSex, setAdministrativeSex] = useState<"female" | "male" | "unknown">(
    initial?.administrativeSex === "female" || initial?.administrativeSex === "male"
      ? initial.administrativeSex
      : "unknown",
  );
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [urgency, setUrgency] = useState<"routine" | "soon" | "urgent">("routine");
  const [coverageKind, setCoverageKind] = useState<"cash" | "insurance">(
    initial?.coverage === undefined ? "cash" : "insurance",
  );
  const [payerKind, setPayerKind] = useState<CoveragePayerKind>("provincial_plan");
  const [payerName, setPayerName] = useState(initial?.coverage?.payerName ?? "");
  const [memberId, setMemberId] = useState(initial?.coverage?.memberId ?? "");
  const [groupNumber, setGroupNumber] = useState("");
  const [relationship, setRelationship] = useState<"self" | "spouse" | "child" | "other">("self");
  // The health card's version code and expiry date: only a provincial plan
  // has them, so ones typed before switching plan stay on screen but never travel.
  const [versionCode, setVersionCode] = useState(initial?.coverage?.versionCode ?? "");
  const [expiryDate, setExpiryDate] = useState(initial?.coverage?.expiryDate ?? "");
  const [photo, setPhoto] = useState<PreparedHealthCardPhoto | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setValidationError(null);
    const candidate = {
      legalFirstName: legalFirstName.trim(),
      ...(legalMiddleName.trim() === "" ? {} : { legalMiddleName: legalMiddleName.trim() }),
      legalLastName: legalLastName.trim(),
      ...(preferredName.trim() === "" ? {} : { preferredName: preferredName.trim() }),
      dateOfBirth: dateOfBirth.trim(),
      administrativeSex,
      ...(phone.trim() === "" ? {} : { phone: phone.trim() }),
      ...(email.trim() === "" ? {} : { email: email.trim() }),
      chiefComplaint: chiefComplaint.trim(),
      urgency,
      coverage:
        coverageKind === "cash"
          ? { kind: "cash" as const }
          : {
              kind: "insurance" as const,
              payerName: payerName.trim(),
              payerKind,
              memberId: memberId.trim(),
              ...(groupNumber.trim() === "" ? {} : { groupNumber: groupNumber.trim() }),
              relationship,
              ...(payerKind === "provincial_plan" && versionCode.trim() !== ""
                ? { versionCode: versionCode.trim().toUpperCase() }
                : {}),
              ...(payerKind === "provincial_plan" && expiryDate.trim() !== ""
                ? { expiryDate: expiryDate.trim() }
                : {}),
            },
    };
    const parsed = WalkInIntakeInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(
        issue === undefined
          ? "The walk-in intake is incomplete."
          : `${issue.path.join(".") || "intake"}: ${issue.message}`,
      );
      return;
    }
    // Like the version code, a photo taken before switching plan never travels.
    const provincial = coverageKind === "insurance" && payerKind === "provincial_plan";
    if (photo !== null && provincial && healthCardPhotos !== undefined) {
      onSubmit(parsed.data, photo);
    } else {
      onSubmit(parsed.data);
    }
  };

  return (
    <section
      className={embedded ? "pp-walk-in-new" : "pp-register pp-walk-in"}
      aria-labelledby={titleId}
    >
      {embedded ? (
        <h3 id={titleId}>Register new patient</h3>
      ) : (
        <header className="pp-register-head">
          <div>
            <p className="pp-eyebrow">Persisted intake</p>
            <h2 id={titleId}>Register a walk-in</h2>
          </div>
          <button
            type="button"
            className="pp-modal-close"
            aria-label="Close walk-in form"
            onClick={onCancel}
          >
            <IconClose />
          </button>
        </header>
      )}
      <form id={formId} className="pp-form pp-editor" onSubmit={submit}>
        <fieldset className="pp-fieldset" disabled={submitting || disabled}>
          <legend className="sr-only">Walk-in patient details</legend>
          <div className="pp-editor-fields">
            <label className="pp-field">
              <span>Legal first name</span>
              <input
                required
                value={legalFirstName}
                onChange={(event) => setLegalFirstName(event.target.value)}
              />
            </label>
            <label className="pp-field">
              <span>Legal middle name (optional)</span>
              <input
                value={legalMiddleName}
                onChange={(event) => setLegalMiddleName(event.target.value)}
              />
            </label>
            <label className="pp-field">
              <span>Legal last name</span>
              <input
                required
                value={legalLastName}
                onChange={(event) => setLegalLastName(event.target.value)}
              />
            </label>
            <label className="pp-field">
              <span>Preferred name (optional)</span>
              <input
                value={preferredName}
                onChange={(event) => setPreferredName(event.target.value)}
              />
            </label>
            <label className="pp-field">
              <span>Date of birth</span>
              <input
                type="date"
                required
                value={dateOfBirth}
                onChange={(event) => setDateOfBirth(event.target.value)}
              />
            </label>
            <fieldset className="pp-walk-in-sex">
              <legend>Administrative sex</legend>
              <div>
                {(["male", "female"] as const).map((sex) => (
                  <label key={sex}>
                    <input
                      type="checkbox"
                      checked={administrativeSex === sex}
                      onChange={(event) =>
                        setAdministrativeSex(event.target.checked ? sex : "unknown")
                      }
                    />
                    {sex === "male" ? "Male" : "Female"}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="pp-field">
              <span>Phone (optional)</span>
              <input value={phone} onChange={(event) => setPhone(event.target.value)} />
            </label>
            <label className="pp-field">
              <span>Email (optional)</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="pp-field pp-field-wide">
              <span>Chief complaint</span>
              <textarea
                required
                rows={2}
                value={chiefComplaint}
                onChange={(event) => setChiefComplaint(event.target.value)}
              />
            </label>
            <label className="pp-field">
              <span>Urgency</span>
              <select
                value={urgency}
                onChange={(event) => setUrgency(event.target.value as typeof urgency)}
              >
                <option value="routine">Routine</option>
                <option value="soon">Soon</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label className="pp-field">
              <span>Coverage</span>
              <select
                value={coverageKind}
                onChange={(event) => setCoverageKind(event.target.value as typeof coverageKind)}
              >
                <option value="cash">Cash</option>
                <option value="insurance">Insurance</option>
              </select>
            </label>
            {coverageKind === "insurance" ? (
              <>
                <label className="pp-field">
                  <span>Coverage type</span>
                  <select
                    value={payerKind}
                    onChange={(event) => setPayerKind(event.target.value as CoveragePayerKind)}
                  >
                    {COVERAGE_PAYER_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {COVERAGE_PAYER_KIND_LABELS[kind]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="pp-field">
                  <span>Payer name</span>
                  <input
                    required
                    value={payerName}
                    onChange={(event) => setPayerName(event.target.value)}
                  />
                </label>
                <label className="pp-field">
                  <span>
                    {payerKind === "provincial_plan" || payerKind === "federal_program"
                      ? "Health card / member ID"
                      : "Member ID"}
                  </span>
                  <input
                    required
                    value={memberId}
                    onChange={(event) => setMemberId(event.target.value)}
                  />
                </label>
                {payerKind === "provincial_plan" ? (
                  <>
                    <label className="pp-field">
                      <span>Version code (optional)</span>
                      <input
                        maxLength={2}
                        placeholder="AB"
                        value={versionCode}
                        onChange={(event) => setVersionCode(event.target.value.toUpperCase())}
                      />
                    </label>
                    <label className="pp-field">
                      <span>Card expiry date (optional)</span>
                      <input
                        type="date"
                        value={expiryDate}
                        onChange={(event) => setExpiryDate(event.target.value)}
                      />
                    </label>
                    {healthCardPhotos !== undefined ? (
                      <HealthCardPhotoField
                        client={healthCardPhotos.client}
                        siteId={healthCardPhotos.siteId}
                        photo={photo}
                        onPhotoChange={setPhoto}
                        disabled={submitting || disabled}
                      />
                    ) : null}
                  </>
                ) : null}
                <label className="pp-field">
                  <span>Group number (optional)</span>
                  <input
                    value={groupNumber}
                    onChange={(event) => setGroupNumber(event.target.value)}
                  />
                </label>
                <label className="pp-field">
                  <span>Relationship to subscriber</span>
                  <select
                    value={relationship}
                    onChange={(event) => setRelationship(event.target.value as typeof relationship)}
                  >
                    <option value="self">Self</option>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </>
            ) : null}
          </div>
        </fieldset>
        {validationError ? (
          <p className="pp-field-error" role="alert">
            {validationError}
          </p>
        ) : null}
        {error ? (
          <p className="pp-field-error" role="alert">
            {error}
          </p>
        ) : null}
        {embedded ? null : (
          <div className="pp-editor-actions">
            <button type="submit" className="pp-btn primary" disabled={submitting}>
              {submitting ? "Registering walk-in…" : "Register walk-in"}
            </button>
            <button type="button" className="pp-btn ghost" onClick={onCancel}>
              Cancel
            </button>
          </div>
        )}
      </form>
    </section>
  );
}
